import { useContext, useEffect, useMemo, useState } from "react";

import {
  PaymentScriptProviderContext,
  initialLoadStateForLocal,
} from "checkout-experience/components/payment-method/payment-script-provider";
import { initializePaze } from "checkout-experience/hooks/helpers/initialize-paze";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { useCheckoutSession } from "checkout-query-client/hooks/use-checkout-session";
import { PaymentMethod } from "checkout-query-client/hooks/use-merchant-settings";
import { PaymentType } from "checkout-query-client/types/form-types";
import { hasMopForCurrency } from "checkout-query-client/utils/helpers";

import { Paze, PlainMessage } from "frontend-client";

import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

export const useEnabledPaymentMethods = () => {
  const [isPazeInitializeCalled, setIsPazeInitializeCalled] = useState(false);
  const [isPazeAllowed, setIsPazeAllowed] = useState(false);
  const [paymentMethodsList, setPaymentMethodsList] = useState<PaymentMethod[]>(
    []
  );
  const [isPaymentMethodsListInitialized, setIsPaymentMethodsListInitialized] =
    useState(false);
  const [isPaymentMethodsListLoading, setIsPaymentMethodsListLoading] =
    useState(false);
  const [isPaymentMethodsListLoaded, setIsPaymentMethodsListLoaded] =
    useState(false);
  const {
    merchantSettings,
    pazeSDK,
    target,
    apiHost = "",
    version = "",
    checkoutSessionToken = "",
  } = useChexContext();

  const { data: checkoutSession } = useCheckoutSession({
    apiHost,
    version,
    checkoutSessionToken,
  });

  const { currencyCode = "USD" } = checkoutSession ?? {};
  const { paymentMethods } = merchantSettings || {};
  const {
    paymentProviders: {
      googlePayPaymentMethod: googlePayScriptState,
      applePayPaymentMethod: applePayScriptState,
      pazePaymentMethod: pazeScriptState,
      payByBankPaymentMethod: payByBankScriptState,
    },
  } = useContext(PaymentScriptProviderContext);

  function updatePaymentMethod(
    paymentType: PaymentType,
    scriptState: { isLoaded: boolean; isLoading: boolean; isError: boolean }
  ) {
    setPaymentMethodsList((prevPaymentMethodList) => [
      ...prevPaymentMethodList.map((paymentMethod) => {
        if (paymentMethod.name === paymentType) {
          // sendCustomTiming({
          //   name: "payment_method_load" + paymentType + (scriptState.isError ? "_failed" : "_success")
          // }); // TODO: This function fires more than once and these timings will have dupes
          return {
            ...paymentMethod,
            active: scriptState.isLoaded && !scriptState.isError ? true : false,
            isLoaded: scriptState.isLoaded,
            isError: scriptState.isError,
            isLoading: false,
          };
        }
        return paymentMethod;
      }),
    ]);
  }

  function isPaymentMethodInactive(
    paymentType: PaymentType,
    list: PaymentMethod[]
  ) {
    return list.find((wallet) => wallet.name === paymentType && !wallet.active);
  }

  // Google Payment Method
  useEffect(() => {
    if (
      (googlePayScriptState.isLoaded &&
        isPaymentMethodInactive(PaymentType.googlePay, paymentMethodsList) &&
        window.google) ||
      googlePayScriptState.isError
    ) {
      updatePaymentMethod(PaymentType.googlePay, googlePayScriptState);
    }
  }, [
    JSON.stringify(googlePayScriptState),
    JSON.stringify(
      paymentMethodsList.filter(
        (paymentMethod) => paymentMethod.name === PaymentType.googlePay
      )
    ),
  ]);

  // Apple Payment Method
  useEffect(() => {
    if (
      (applePayScriptState.isLoaded &&
        isPaymentMethodInactive(PaymentType.applePay, paymentMethodsList) &&
        window.ApplePaySession &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        window.ApplePaySession?.canMakePayments()) ||
      applePayScriptState.isError
    ) {
      updatePaymentMethod(PaymentType.applePay, applePayScriptState);
    }
  }, [
    JSON.stringify(applePayScriptState),
    JSON.stringify(
      paymentMethodsList.filter(
        (paymentMethod) => paymentMethod.name === PaymentType.applePay
      )
    ),
  ]);

  // Pay by Bank Payment Method
  useEffect(() => {
    if (payByBankScriptState.isLoaded || applePayScriptState.isError) {
      // activate for pay by bank iframe version
      updatePaymentMethod(PaymentType.payByBank, payByBankScriptState);
    }
  }, [
    JSON.stringify(payByBankScriptState),
    JSON.stringify(
      paymentMethodsList.filter(
        (paymentMethod) => paymentMethod.name === PaymentType.payByBank
      )
    ),
  ]);

  // Paze Payment Method
  useEffect(() => {
    if (
      (isPazeAllowed &&
        isPaymentMethodInactive(PaymentType.paze, paymentMethodsList)) ||
      pazeScriptState.isError
    ) {
      updatePaymentMethod(PaymentType.paze, pazeScriptState);
    }
  }, [
    isPazeAllowed,
    JSON.stringify(
      paymentMethodsList.filter(
        (paymentMethod) => paymentMethod.name === PaymentType.paze
      )
    ),
  ]);

  const isMerchantEligibleForPaze = useMemo(() => {
    return merchantSettings?.country === "US" && currencyCode === "USD";
  }, [merchantSettings?.country, currencyCode]);

  const pazeClientId = useMemo(() => {
    return (
      paymentMethodsList.find((p) => p.name === PaymentType.paze)
        ?.data as PlainMessage<Paze>
    )?.clientId;
  }, [JSON.stringify(paymentMethodsList)]);

  useEffect(() => {
    if (
      pazeScriptState.isLoaded &&
      isMerchantEligibleForPaze &&
      pazeClientId &&
      !isPazeInitializeCalled
    ) {
      setIsPazeInitializeCalled(true);
      initializePaze(pazeClientId, merchantSettings?.merchantDisplayName ?? "")
        .then(() => {
          setIsPazeAllowed(true);
        })
        .catch(() => {
          setIsPazeAllowed(false);
          // Mark payment method in error
          updatePaymentMethod(PaymentType.paze, {
            isLoading: false,
            isLoaded: false,
            isError: true,
          });
        });
    }
  }, [
    isMerchantEligibleForPaze,
    pazeClientId,
    pazeScriptState,
    isPazeInitializeCalled,
  ]);

  // Map merchant settings to list of enabled MOPS
  useEffect(() => {
    const list = [
      ...(paymentMethods?.cardPaymentMethod?.enabled &&
      hasMopForCurrency(currencyCode, paymentMethods?.cardPaymentMethod)
        ? [
            {
              name: PaymentType.creditCard,
              data: paymentMethods?.cardPaymentMethod,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
      ...(paymentMethods?.ecpPaymentMethod?.enabled &&
      hasMopForCurrency(currencyCode, paymentMethods?.ecpPaymentMethod)
        ? [
            {
              name: PaymentType.eCheck,
              data: paymentMethods?.ecpPaymentMethod,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
      ...(paymentMethods?.sepaPaymentMethod?.enabled
        ? [
            {
              name: PaymentType.sepa,
              data: paymentMethods?.sepaPaymentMethod,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
      ...(paymentMethods?.walletPaymentMethod?.googlePay?.enabled
        ? [
            {
              name: PaymentType.googlePay,
              data: paymentMethods?.walletPaymentMethod?.googlePay,
              script: {
                id: PaymentType.googlePay,
                crossOrigin: false,
                url: "https://pay.google.com/gp/p/js/pay.js",
              },
              active: false,
              ...googlePayScriptState,
            },
          ]
        : []),
      ...(paymentMethods?.walletPaymentMethod?.applePay?.enabled
        ? [
            {
              name: PaymentType.applePay,
              data: paymentMethods?.walletPaymentMethod?.applePay,
              script: {
                id: PaymentType.applePay,
                crossOrigin: true,
                url: getIsFeatureEnabled(target ?? "", "apple-pay-latest-sdk")
                  ? "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js"
                  : "https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js",
              },
              active: false,
              ...applePayScriptState,
            },
          ]
        : []),
      ...(paymentMethods?.walletPaymentMethod?.paze?.enabled
        ? [
            {
              name: PaymentType.paze,
              data: paymentMethods?.walletPaymentMethod?.paze,
              script: {
                id: PaymentType.paze,
                crossOrigin: false,
                url: pazeSDK ?? "",
              },
              active: false,
              ...pazeScriptState,
            },
          ]
        : []),
      ...(paymentMethods?.payByBankPaymentMethod?.enabled
        ? [
            {
              name: PaymentType.payByBank,
              data: paymentMethods?.payByBankPaymentMethod,
              script: {
                id: PaymentType.payByBank,
                crossOrigin: false,
                url: "https://connect2.finicity.com/assets/sdk/finicity-connect.min.js",
              },
              active: false,
              ...payByBankScriptState,
            },
          ]
        : []),
      ...(paymentMethods?.altPaymentMethod?.ideal?.enabled &&
      hasMopForCurrency(currencyCode, paymentMethods?.altPaymentMethod.ideal)
        ? [
            {
              name: PaymentType.ideal,
              data: paymentMethods?.altPaymentMethod?.ideal,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
      ...(paymentMethods?.altPaymentMethod?.paypal?.enabled &&
      hasMopForCurrency(currencyCode, paymentMethods?.altPaymentMethod.paypal)
        ? [
            {
              name: PaymentType.paypal,
              data: paymentMethods?.altPaymentMethod?.paypal,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
      ...(paymentMethods?.altPaymentMethod?.wechatPay?.enabled &&
      hasMopForCurrency(
        currencyCode,
        paymentMethods?.altPaymentMethod.wechatPay
      )
        ? [
            {
              name: PaymentType.wechatPay,
              data: paymentMethods?.altPaymentMethod?.wechatPay,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
      ...(paymentMethods?.altPaymentMethod?.trustly?.enabled &&
      hasMopForCurrency(currencyCode, paymentMethods?.altPaymentMethod.trustly)
        ? [
            {
              name: PaymentType.trustly,
              data: paymentMethods?.altPaymentMethod?.trustly,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
      ...(paymentMethods?.altPaymentMethod?.alipay?.enabled &&
      hasMopForCurrency(currencyCode, paymentMethods?.altPaymentMethod.alipay)
        ? [
            {
              name: PaymentType.alipay,
              data: paymentMethods?.altPaymentMethod?.alipay,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
      ...(paymentMethods?.giftCardPaymentMethod?.enabled
        ? [
            {
              name: PaymentType.giftCard,
              data: paymentMethods?.giftCardPaymentMethod,
              active: true,
              ...initialLoadStateForLocal,
            },
          ]
        : []),
    ];
    setPaymentMethodsList(list);
  }, [JSON.stringify(paymentMethods), currencyCode, pazeSDK]);

  const activePaymentMethodsList = [
    ...paymentMethodsList.filter(
      (paymentMethod: PaymentMethod) => paymentMethod.active
    ),
  ];

  useEffect(() => {
    if (
      !isPaymentMethodsListInitialized &&
      merchantSettings &&
      paymentMethodsList
    ) {
      setIsPaymentMethodsListInitialized(true);
    }
  }, [merchantSettings, paymentMethodsList]);

  useEffect(() => {
    if (paymentMethodsList.length > 0) {
      setIsPaymentMethodsListLoading(
        paymentMethodsList.every((mop) => mop.isLoading)
      );
      setIsPaymentMethodsListLoaded(
        paymentMethodsList.every((mop) => mop.isLoaded || mop.isError)
      );
    }
  }, [paymentMethodsList]);

  return {
    activePaymentMethodsList,
    paymentMethodsList,
    isPaymentMethodsListInitialized,
    isPaymentMethodsListLoading,
    isPaymentMethodsListLoaded,
  };
};
